import React from "react";
import { ChevronUp, ChevronDown, Plus, Trash2 } from "lucide-react";
import { moveArrayItem } from "./moveArrayItem";

const BLANK_QUESTION = {
  question: "",
  key: "",
  internal_goal: "",
  turn_on: true,
  optional: false,
};

const FIELD_CLASS =
  "w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body";

function asQuestionRow(q) {
  return q && typeof q === "object" && !Array.isArray(q) ? q : {};
}

/**
 * @param {{
 *   questions?: unknown,
 *   onChange?: (next: Array) => void,
 * }} props
 */
export function QuestionnaireEditor({ questions = [], onChange }) {
  const list = Array.isArray(questions) ? questions : [];
  const emit = typeof onChange === "function" ? onChange : () => {};

  const updateField = (idx, field, value) => {
    emit(
      list.map((q, i) =>
        i === idx ? { ...asQuestionRow(q), [field]: value } : q
      )
    );
  };

  return (
    <>
      <div className="space-y-4">
        {list.map((q, idx) => {
          const row = asQuestionRow(q);
          return (
            <div
              key={idx}
              data-testid={`questionnaire-card-${idx}`}
              className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3"
            >
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  {list.length > 1 && (
                    <div className="flex flex-col">
                      <button
                        type="button"
                        onClick={() => emit(moveArrayItem(list, idx, -1))}
                        disabled={idx === 0}
                        aria-label="Move question up"
                        data-testid={`questionnaire-move-up-${idx}`}
                        className={`p-1 transition-colors ${
                          idx === 0
                            ? "text-[#9CA3AF] opacity-30 cursor-not-allowed"
                            : "text-[#9CA3AF] hover:text-[#00D1FF]"
                        }`}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => emit(moveArrayItem(list, idx, +1))}
                        disabled={idx === list.length - 1}
                        aria-label="Move question down"
                        data-testid={`questionnaire-move-down-${idx}`}
                        className={`p-1 transition-colors ${
                          idx === list.length - 1
                            ? "text-[#9CA3AF] opacity-30 cursor-not-allowed"
                            : "text-[#9CA3AF] hover:text-[#00D1FF]"
                        }`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <span className="font-mono text-[#00D1FF] text-xs">
                    Question {idx + 1}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <span className="text-xs font-body text-[#9CA3AF]">Active</span>
                    <button
                      type="button"
                      onClick={() => updateField(idx, "turn_on", !row.turn_on)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        row.turn_on ? "bg-[#00D1FF]" : "bg-white/20"
                      }`}
                      aria-pressed={!!row.turn_on}
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                          row.turn_on ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </label>
                  <label className="flex items-center gap-2">
                    <span className="text-xs font-body text-[#9CA3AF]">Optional</span>
                    <button
                      type="button"
                      onClick={() => updateField(idx, "optional", !row.optional)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        row.optional ? "bg-amber-400" : "bg-white/20"
                      }`}
                      aria-pressed={!!row.optional}
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                          row.optional ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </label>
                  {list.length > 1 && (
                    <button
                      type="button"
                      onClick={() => emit(list.filter((_, i) => i !== idx))}
                      className="text-[#9CA3AF] hover:text-red-400 transition-colors p-1"
                      aria-label="Remove question"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <textarea
                value={row.question || ""}
                onChange={(e) => updateField(idx, "question", e.target.value)}
                rows={2}
                placeholder="Question the Digital Brain asks..."
                className={`${FIELD_CLASS} resize-y`}
              />
              <input
                type="text"
                value={row.key || ""}
                onChange={(e) => updateField(idx, "key", e.target.value)}
                placeholder="Data key (e.g., property_address)"
                className={FIELD_CLASS}
              />
              <input
                type="text"
                value={row.internal_goal || ""}
                onChange={(e) => updateField(idx, "internal_goal", e.target.value)}
                placeholder="Internal goal (e.g., Service area check)"
                className={FIELD_CLASS}
              />
            </div>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => emit([...list, { ...BLANK_QUESTION }])}
        data-testid="questionnaire-add-question"
        className="flex items-center gap-2 text-[#00D1FF] text-sm font-body hover:text-[#00D1FF]/80 transition-colors py-2"
      >
        <Plus className="w-4 h-4" />
        Add Question
      </button>
    </>
  );
}
